import { useCallback, useEffect, useRef, useState } from "react";
import { useChime } from "./useChime";

// "AV12" -> "A. V. 12": la voce scandisce le lettere e poi il numero.
const pronuncia = (codice) => {
  const lettere = (String(codice).match(/[A-Za-z]/g) || []).join(". ");
  const numero = (String(codice).match(/\d+/) || [""])[0];
  return [lettere, numero].filter(Boolean).join(". ");
};

// "Jenner" letto all'italiana con la G dolce (Genner).
const fonetica = (testo) => String(testo).replace(/jenner/gi, "Genner");

const PAUSA_TRA_ANNUNCI = 400;
const DURATA_DONG = 1600;

// Annuncio vocale del codice sul solo schermo di sala: gli annunci vengono messi in
// coda e letti uno dopo l'altro, mai sovrapposti. Ripiega sul dong se la voce manca.
export function useAnnunciatore() {
  const { play: playChime, needsUnlock, unlock } = useChime();
  const [enabled, setEnabled] = useState(true);
  const [voceDisponibile, setVoceDisponibile] = useState(false);
  const voceRef = useRef(null);
  const enabledRef = useRef(true);
  const codaRef = useRef([]);
  const inCorsoRef = useRef(false);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    const scegli = () => {
      const voci = synth.getVoices() || [];
      const it = voci.filter((v) => /^it/i.test(v.lang));
      voceRef.current =
        it.find((v) => /google/i.test(v.name)) || it.find((v) => v.localService) || it[0] || null;
      setVoceDisponibile(!!voceRef.current);
    };
    scegli();
    synth.addEventListener?.("voiceschanged", scegli);
    const t = setTimeout(scegli, 1200);
    return () => {
      clearTimeout(t);
      synth.removeEventListener?.("voiceschanged", scegli);
    };
  }, []);

  const consuma = useCallback(() => {
    if (inCorsoRef.current) return;
    const testo = codaRef.current.shift();
    if (!testo) return;
    inCorsoRef.current = true;

    const fine = () => {
      if (!inCorsoRef.current) return;
      inCorsoRef.current = false;
      setTimeout(() => consuma(), PAUSA_TRA_ANNUNCI);
    };

    const synth = window.speechSynthesis;
    if (!synth || !voceRef.current) {
      playChime();
      setTimeout(fine, DURATA_DONG);
      return;
    }
    try {
      const u = new SpeechSynthesisUtterance(fonetica(testo));
      u.voice = voceRef.current;
      u.lang = voceRef.current.lang || "it-IT";
      u.rate = 0.88;
      u.pitch = 1;
      u.volume = 1;
      u.onend = fine;
      u.onerror = fine;
      synth.speak(u);
      // Rete di sicurezza: se il browser non emette onend non blocchiamo la coda.
      setTimeout(fine, 9000);
    } catch {
      playChime();
      setTimeout(fine, DURATA_DONG);
    }
  }, [playChime]);

  const accoda = useCallback(
    (testo) => {
      codaRef.current.push(testo);
      consuma();
    },
    [consuma]
  );

  const annuncia = useCallback(
    (codice, sala) => {
      if (!enabledRef.current || !codice) return;
      accoda(`Codice ${pronuncia(codice)}. ${sala || ""}`.trim());
    },
    [accoda]
  );

  const provaVoce = useCallback(() => {
    accoda("Prova voce. Codice A. V. 1. Sala Jenner");
  }, [accoda]);

  return { annuncia, provaVoce, enabled, setEnabled, voceDisponibile, needsUnlock, unlock };
}
